import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import TopActionBar from '../../../uikit/top_actionbar';
import Button from '../../../uikit/button';
import Input from '../../../uikit/input';
import { API_ASSET_BASE_URL } from '../../../apiConstant';
import { useS3FilesByType } from '../../../utils/hooks/useS3FilesByType';
import { FILE_TYPES } from '../../../utils/constants';
import DragDrop2 from '../../../uikit/drag_and_drop/DargDrop2';
import DisplayFiles from '../../project/components/pages/labour-payments/DisplayFiles';

const REQUIRED_FIELDS = ['providerName', 'insuranceName', 'policyNumber', 'startDate', 'endDate'];

export default function InsuranceCreate() {
  const navigate = useNavigate();
  const { id: insuranceID } = useParams();
  const uploadRef = useRef(null);
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    providerName: '',
    insuranceName: '',
    policyName: '',
    coverageAmount: '',
    startDate: '',
    endDate: '',
    policyNumber: '',
    remarks: '',
  });

  const { data: files = [] } = useS3FilesByType({
    type: FILE_TYPES.INSURANCE_DOCUMENTS,
    id: insuranceID,
    enabled: !!insuranceID,
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // clear the field's error as soon as the user edits it
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = () => {
    const newErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      const value = formData[field];
      if (value === null || value === undefined || String(value).trim() === '') {
        newErrors[field] = `${field} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const createInsuranceMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await axios.post(`${API_ASSET_BASE_URL}/api/insurances`, payload);
      return res.data;
    },
    onSuccess: async (data) => {

      const Id = data?.data?.id;

      if (Id && uploadRef.current) {
        await uploadRef.current.triggerUpload(Id);
      }
      queryClient.invalidateQueries(['insurances']);
      window.triggerAlert?.({ title: 'Insurance created successfully', type: 'success' });
      navigate(-1);
    },
    onError: (err) => {
      window.triggerAlert?.({ title: 'Failed to create insurance', type: 'error' });
      console.error(err);
    }
  });

  const updateInsuranceMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await axios.put(`${API_ASSET_BASE_URL}/api/insurances/${insuranceID}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['insurances']);
      window.triggerAlert?.({ title: 'Insurance updated successfully', type: 'success' });
      navigate(-1);
    },
    onError: (err) => {
      window.triggerAlert?.({ title: 'Failed to update insurance', type: 'error' });
      console.error(err);
    }
  });

  const { data: insurance, isLoading: isInsuranceLoading } = useQuery({
    queryKey: ['insurance', insuranceID],
    queryFn: async () => {
      const res = await axios.get(`${API_ASSET_BASE_URL}/api/insurances/${insuranceID}`);
      return res.data?.data || null;
    },
    enabled: !!insuranceID,
  });

  useEffect(() => {
    if (insurance) {
      setFormData({
        providerName: insurance.providerName || '',
        insuranceName: insurance.insuranceName || '',
        policyName: insurance.policyName || '',
        coverageAmount: insurance.coverageAmount ?? '',
        startDate: insurance.startDate || '',
        endDate: insurance.endDate || '',
        policyNumber: insurance.policyNumber || '',
        remarks: insurance.remarks || '',
      });
    }
  }, [insurance]);

  const handleSave = () => {
    if (!validate()) {
      window.triggerAlert?.({ title: 'Please fill all required fields', type: 'error' });
      return;
    }

    const payload = {
      providerName: formData.providerName,
      insuranceName: formData.insuranceName,
      policyName: formData.policyName,
      coverageAmount: formData.coverageAmount ? Number(formData.coverageAmount) : null,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
      policyNumber: formData.policyNumber,
      remarks: formData.remarks || null,
    };

    if (insuranceID) {
      updateInsuranceMutation.mutate(payload);
    } else {
      createInsuranceMutation.mutate(payload);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  return (
    <>
      <TopActionBar
        leftContent={
          <div className="flex items-center text-white gap-4">
            <Button
              containerClass="md:block hidden"
              onClick={goBack}
              startIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="6"
                  height="11"
                  fill="none"
                >
                  <path
                    d="M5 10L1 5.5L5 1"
                    stroke="#FFCC00"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              variant="secondaryContained"
            />
            <h2 className="font-semibold text-lg">{insuranceID ? 'Edit Insurance' : 'Add Insurance'}</h2>
          </div>
        }
        rightContent={
          <Button variant="contained" onClick={handleSave} disabled={createInsuranceMutation.isPending || updateInsuranceMutation.isPending}>
            {createInsuranceMutation.isPending || updateInsuranceMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        }
      />

      <>
        <div className="bg-white rounded-lg shadow-sm p-6 w-full max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">

            {/* Left Column */}
            <div>

              <Input
                label="Insurance Name"
                labelClassName='!text-[#A86808]'
                placeholder="Enter Insurance Name"
                value={formData.insuranceName}
                onChange={(e) => handleInputChange('insuranceName', e.target.value)}
                error={errors.insuranceName}
              />
            </div>

            {/* Right Column */}
            <div>
              <Input
                label="Provider Name"
                labelClassName='!text-[#A86808]'
                placeholder="Enter Provider Name"
                value={formData.providerName}
                onChange={(e) => handleInputChange('providerName', e.target.value)}
                error={errors.providerName}
              />
            </div>

            {/* Left Column */}
            <div>
              <Input
                label="Policy Name"
                labelClassName='!text-[#A86808]'
                placeholder="Enter Policy Name"
                value={formData.policyName}
                onChange={(e) => handleInputChange('policyName', e.target.value)}
              />
            </div>

            {/* Right Column */}
            <div>
              <Input
                label="Policy Number"
                labelClassName='!text-[#A86808]'
                placeholder="Enter Policy Number"
                value={formData.policyNumber}
                onChange={(e) => handleInputChange('policyNumber', e.target.value)}
                error={errors.policyNumber}
              />
            </div>



            {/* Left Column */}
            <div>
              <Input
                label="Start Date"
                labelClassName='!text-[#A86808]'
                type="date"
                placeholder="Select Date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                error={errors.startDate}
              />
            </div>

            {/* Right Column */}
            <div>
              <Input
                label="End Date"
                labelClassName='!text-[#A86808]'
                type="date"
                placeholder="Select Date"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                error={errors.endDate}
              />
            </div>
            {/* Left Column */}
            <div>
              <Input
                label="Coverage Amount"
                labelClassName='!text-[#A86808]'
                type="number"
                placeholder="Enter Coverage Amount"
                value={formData.coverageAmount}
                onChange={(e) => handleInputChange('coverageAmount', e.target.value)}
              />
            </div>

            {/* Right Column */}
            <div>
              <Input
                label="Remarks"
                labelClassName='!text-[#A86808]'
                placeholder="Enter here"
                value={formData.remarks}
                onChange={(e) => handleInputChange('remarks', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-base font-bold text-[#A86808] mb-2">Upload Image</label>

              <DragDrop2
                ref={uploadRef}
                type={FILE_TYPES.INSURANCE_DOCUMENTS}
                id={insuranceID}
                onUpload={() => {
                  queryClient.invalidateQueries({
                    queryKey: ['s3-files', FILE_TYPES.INSURANCE_DOCUMENTS, insuranceID],
                  });
                }}
                variant="small"
                subtitle="PDF, Images and Documents"
              />

            </div>

            {/* Right Column */}
            <div>
              <label className="block text-base font-bold text-[#A86808] mb-2">Uploaded Files</label>
              <DisplayFiles
                files={files}
                onDelete={() => {
                  queryClient.invalidateQueries({
                    queryKey: ['s3-files', FILE_TYPES.INSURANCE_DOCUMENTS, insuranceID],
                  });
                }}
                enableDelete={true}
              />
            </div>

          </div>
        </div>
      </>
    </>
  );
}